// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
    // --- STATE VARIABLES ---
    const [showNavbar, setShowNavbar] = useState(true);
    const [lastScrollPos, setLastScrollPos] = useState(0);
    const [mobileMenu, setMobileMenu] = useState(false);

    // --- HOOKS ---
    const navigate = useNavigate(); 
    const location = useLocation();

    // --- SCROLL EFFECT FOR HIDING/SHOWING NAVBAR ---
    useEffect(() => {
        const handleScroll = () => {
            const currentScroll = window.pageYOffset;
            setShowNavbar(currentScroll < lastScrollPos || currentScroll < 20);
            setLastScrollPos(currentScroll);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollPos]);

    // Prevent scrolling when mobile menu is open
    useEffect(() => {
        if (mobileMenu) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [mobileMenu]);

    // --- CUSTOM HANDLER FOR SCROLLING TO SECTIONS ---
    const handleScrollToSection = (targetId) => {
        setMobileMenu(false);

        if (location.pathname !== '/') {
            navigate(`/#${targetId}`);
        } else {
            const targetElement = document.getElementById(targetId);
            if (targetElement) {
                setTimeout(() => {
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    };

    return (
        <header className={`fixed top-0 left-0 w-full z-[100] transition-transform duration-500 ease-in-out ${showNavbar ? 'translate-y-0' : '-translate-y-full'}`}>

            {/* ======================= */}
            {/* DESKTOP HEADER (LG+)    */}
            {/*======================= */}
            <div className="hidden lg:block px-6 py-1">
                <div className="max-w-[1750px] mx-auto flex flex-col gap-1">
                    {/* TOP BAR */}
                    <div className="flex justify-end pr-4">
                        <div className="bg-slate-900/95 backdrop-blur-md px-6 py-2 rounded-full flex gap-8 shadow-2xl items-center border border-white/10">
                            <div className="flex items-center gap-3 border-r border-white/10 pr-8">
                                <div className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500 shadow-[0_0_12px_#f97316]"></span>
                                </div>
                                <span className="text-[9px] font-black uppercase tracking-[0.3em] text-white/90">Hiring Active</span>
                            </div>
                        </div>
                    </div>

                    {/* MAIN NAVIGATION */}
                    <div className="flex items-center gap-5">
                        <div className="bg-white rounded-r-[2.5rem] rounded-l-none pl-12 pr-10 py-4 shadow-[15px_10px_40px_rgba(0,0,0,0.1)] border-y border-r border-slate-100 flex items-center -ml-6 shrink-0">
                            <Link to="/" className="relative group">
                                <img src="/images/logo.png" alt="Acadfinity" className="h-20 w-auto object-contain transition-transform duration-500 group-hover:scale-105" />
                            </Link>
                        </div>
                        <nav className="bg-white/95 backdrop-blur-2xl rounded-full px-10 py-3 flex-1 flex justify-between items-center shadow-[0_15px_40px_rgba(0,0,0,0.05)] border border-slate-100 relative mr-4">
                            <div className="flex items-center gap-10">
                                <Link to="/" className="relative text-[14px] font-black uppercase tracking-[0.2em] py-1 text-slate-500 hover:text-brand-orange">Home</Link>
                                <Link to="/AboutUs" className="text-[14px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-brand-orange transition-all">About</Link>
                                
                                {/* --- NEW LINK FOR SCHOOLS --- */}
                                <Link to="/for-schools" className="text-[14px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-brand-orange transition-all">
                                    Institutions
                                </Link>
                                {/* --------------------------- */}

                                <button onClick={() => handleScrollToSection('FAQ')} className="text-[14px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-brand-orange transition-all flex items-center gap-2 bg-transparent border-none cursor-pointer">
                                    FAQs
                                </button>
                                <button onClick={() => handleScrollToSection('testimonials')} className="text-[14px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-brand-orange transition-all bg-transparent border-none cursor-pointer">
                                    Testimonials
                                </button>
                            </div>

                            <div className="flex items-center gap-6 shrink-0">
                                <div className="w-px h-6 bg-slate-200"></div>
                                <button onClick={() => handleScrollToSection('apply-form')} className="bg-slate-900 text-white px-10 py-3.5 rounded-full text-[10px] font-black uppercase tracking-[0.3em] hover:bg-brand-orange transition-all shadow-xl active:scale-95 shrink-0 border-none cursor-pointer">
                                    Apply Now
                                </button>
                            </div>
                        </nav>
                    </div>
                </div>
            </div>

            {/* ======================= */}
            {/* MOBILE HEADER (SOLID)   */}
            {/* ======================= */}
            <div className="lg:hidden w-full fixed top-0 left-0 z-50 bg-white border-b border-slate-200 shadow-md">
                <div className="px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex-shrink-0">
                        <img src="/images/logo.png" alt="Acadfinity" className="h-12 w-auto" />
                    </Link>
                    <div className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500 shadow-[0_0_12px_#f97316]"></span>
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-black/90">Hiring Active</span>

                    <div className="flex items-center gap-4">
                        <button onClick={() => handleScrollToSection('apply-form')} className="hidden sm:block bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-6 py-3 rounded-full shadow-lg active:scale-95 transition-transform border-none cursor-pointer">
                            Apply Now
                        </button>

                        <button onClick={() => setMobileMenu(true)} className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 hover:bg-orange-50 hover:text-brand-orange transition-colors">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 6h16M4 12h16M4 18h16"></path></svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* ======================= */}
            {/* MOBILE MENU (FULL HEIGHT) */}
            {/* ======================= */}
            {mobileMenu && (
                <div className="fixed inset-0 z-[200] lg:hidden">
                    <div onClick={() => setMobileMenu(false)} className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"></div>

                    <div className="absolute inset-y-0 right-0 w-full sm:max-w-md bg-white shadow-2xl flex flex-col h-[100dvh] animate-in slide-in-from-right duration-300">

                        <div className="px-6 py-5 flex items-center justify-between border-b border-slate-100 bg-white">
                            <img src="/images/logo.png" alt="Acadfinity" className="h-12 w-auto" />
                            <button onClick={() => setMobileMenu(false)} className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-white">
                            <nav className="flex flex-col space-y-3">
                                <Link to="/" onClick={() => setMobileMenu(false)} className="px-4 py-4 rounded-xl text-xl font-bold text-slate-800 hover:bg-orange-50 hover:text-brand-orange transition-colors flex justify-between items-center group">
                                    Home
                                    <svg className="w-5 h-5 text-slate-300 group-hover:text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </Link>
                                <Link to="/AboutUs" onClick={() => setMobileMenu(false)} className="px-4 py-4 rounded-xl text-xl font-bold text-slate-800 hover:bg-orange-50 hover:text-brand-orange transition-colors flex justify-between items-center group">
                                    About
                                    <svg className="w-5 h-5 text-slate-300 group-hover:text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </Link>
                                
                                {/* --- NEW LINK MOBILE --- */}
                                <Link to="/for-schools" onClick={() => setMobileMenu(false)} className="px-4 py-4 rounded-xl text-xl font-bold text-slate-800 hover:bg-orange-50 hover:text-brand-orange transition-colors flex justify-between items-center group">
                                    Institutions
                                    <svg className="w-5 h-5 text-slate-300 group-hover:text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </Link>
                                {/* ----------------------- */}

                                <button onClick={() => handleScrollToSection('FAQ')} className="px-4 py-4 rounded-xl text-xl font-bold text-slate-800 hover:bg-orange-50 hover:text-brand-orange transition-colors flex justify-between items-center group text-left bg-transparent border-none cursor-pointer">
                                    FAQs
                                    <svg className="w-5 h-5 text-slate-300 group-hover:text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <button onClick={() => handleScrollToSection('testimonials')} className="px-4 py-4 rounded-xl text-xl font-bold text-slate-800 hover:bg-orange-50 hover:text-brand-orange transition-colors flex justify-between items-center group bg-transparent border-none cursor-pointer">
                                    Testimonials
                                    <svg className="w-5 h-5 text-slate-300 group-hover:text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                                </button>
                            </nav>

                            <div className="my-8 border-t border-slate-100"></div>

                            <div className="space-y-4 px-4">
                                <button onClick={() => handleScrollToSection('apply-form')} className="block w-full text-center py-4 bg-brand-orange text-white rounded-xl text-sm font-black uppercase tracking-widest shadow-lg shadow-orange-500/20 active:scale-95 transition-transform border-none cursor-pointer">
                                    Apply Now
                                </button>
                            </div>

                            <div className="mt-10 flex justify-center gap-8">
                                <Link to="/AboutUs" onClick={() => setMobileMenu(false)} className="text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-brand-orange">About</Link>
                                <Link to="/contact" onClick={() => setMobileMenu(false)} className="text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-brand-orange">Contact</Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};
export default Header;